import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";
import SuiDatePicker from "components/SuiDatePicker";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import ScheduleIcon from "@mui/icons-material/Schedule";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import InsertInvitationOutlinedIcon from "@mui/icons-material/InsertInvitationOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";

import { DatePicker, Upload } from "antd";
import Prompt from "components/UpStyled/Prompt";
import ColorDropDownMenu from "pages/calendar/components/ColorDropDownMenu";

import { filterParser } from "utils/helper";
import { colorOptions } from "utils/data";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

const CreateCalendarModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      isEdit = false,
      calendar = {},
      calendarOptions = [],
      handleRefresh = () => {},
      handleAfterOk = () => {},
      refreshDelay = 0,
    },
    ref
  ) => {
    const [isActive, setIsActive] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [selectedColor, setSelectedColor] = useState({});

    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      getValues,
      reset,
      watch,
      control,
      formState: { errors },
    } = useForm();

    const account = useContext(AccountContext);

    useEffect(() => {
      if (isEdit) {
        let resetPayload = {
          title: calendar?.label,
          description: calendar?.setting?.description,
        };
        setIsActive(calendar?.active);
        let colorOpt = colorOptions.find((x) => x.value == calendar?.setting?.color);
        if (colorOpt?.value) {
          setSelectedColor(colorOpt);
        } else {
          setSelectedColor({
            label: calendar?.setting?.color,
            value: calendar?.setting?.color,
          });
        }

        reset(resetPayload);
      } else {
        const usedColors = calendarOptions.map((x) => x.color);

        let colorOpt = colorOptions.find((x) => !usedColors.includes(x.value));
        setSelectedColor(colorOpt);
        setIsActive(true);
      }
    }, [isEdit]);

    const handleCreateCalendar = async (data) => {
      try {
        let payload = {
          title: data["title"] || "My Calendar",
          setting: {
            color: selectedColor.value,
            description: data["description"],
          },
        };

        if (isEdit) {
          payload["active"] = data["active"];
          payload["setting"] = {
            ...calendar.setting,
            color: selectedColor.value,
            description: data["description"],
          };
          const newCreatedCalendar = await api.calendar.updateCalendar(calendar?.value, payload);
          toast.success(`${isEdit ? "Edit calendar" : "Create new calendar"} successfully!`);
          handleClose();
          handleAfterOk(newCreatedCalendar);
        } else {
          payload["active"] = true;
          const newCreatedCalendar = await api.calendar.createCalendar(payload);
          console.log(newCreatedCalendar);
          toast.success(`${isEdit ? "Edit calendar" : "Create new calendar"} successfully!`);

          handleClose();
          handleAfterOk(newCreatedCalendar);
        }

        setTimeout(() => {
          handleRefresh();
        }, refreshDelay);
      } catch (err) {
        console.log(err);
        toast.error(`${isEdit ? "Edit calendar" : "Create new calendar"} failed!`);
      }
    };

    const handleConfirmDelete = async () => {
      if (account.id != calendar.owner_id) {
        return toast.error(`You are not the owner of this calendar!`);
      }
      if (calendarOptions.length < 1) {
        return toast.error(`Could not delete the last calendar!`);
      }
      try {
        await api.calendar.deleteCalendar(calendar?.value);
        handleRefresh();
        setDeleteVisible(false);
        handleClose();
        toast.success(`Delete event:${calendar.label} successfully!`);
      } catch (err) {
        toast.error(`Delete event:${calendar?.label} failed!`);
        console.log(err);
      }
    };

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle id="alert-dialog-title">
          {isEdit ? "Edit Calendar" : "Create new calendar"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={handleSubmit(handleOk ? handleOk : handleCreateCalendar)}
          style={{ overflow: "auto" }}
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <SuiBox sx={{ px: 1 }}>
                <SuiBox my={0.5} width="100%">
                  <SuiBox display="flex" alignItems="center" gap={1}>
                    <SuiBox>
                      <InsertInvitationOutlinedIcon fontSize="medium" />
                    </SuiBox>
                    <SuiBox display="flex" gap={1} alignItems="center" width="100%">
                      <SuiBox flexGrow={1}>
                        <SuiInput
                          placeholder="Name"
                          required
                          inputProps={{ maxLength: 200 }}
                          {...register("title")}
                        />{" "}
                      </SuiBox>
                      <ColorDropDownMenu
                        color={selectedColor}
                        onClick={(color) => setSelectedColor(color)}
                      />
                    </SuiBox>
                  </SuiBox>
                </SuiBox>
                <SuiBox my={0.5}>
                  <SuiBox display="flex" alignItems="flex-start" gap={1}>
                    <SuiBox>
                      <SubjectOutlinedIcon fontSize="medium" />
                    </SuiBox>
                    <SuiInput
                      placeholder="Description"
                      rows={4}
                      multiline={true}
                      fullWidth={true}
                      inputProps={{ maxLength: 2000 }}
                      {...register(`description`)}
                    />
                  </SuiBox>
                </SuiBox>
                <SuiBox my={0.5}>
                  <SuiBox display="flex" alignItems="center" gap={1}>
                    <SuiBox display="flex" justifyContent="flex-start" alignItems="center" ml={4}>
                      <Checkbox
                        onChange={(e) => setIsActive(!isActive)}
                        checked={isActive ? true : false}
                      />{" "}
                      Active
                    </SuiBox>
                  </SuiBox>
                </SuiBox>
              </SuiBox>

              <SuiBox
                pt={3}
                px={3}
                display="flex"
                justifyContent="space-between"
                alignItems={"center"}
                gap={1}
              >
                <SuiBox>
                  {calendar?.value && calendarOptions.length > 1 ? (
                    <SuiButton
                      variant="gradient"
                      size="medium"
                      onClick={() => {
                        setDeleteVisible(true);
                      }}
                      color="error"
                    >
                      Delete
                    </SuiButton>
                  ) : null}
                </SuiBox>
                <SuiBox display="flex" gap={1} alignItems="center" justifyContent="end">
                  <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                    {isEdit ? "Save" : "Create"}
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            </DialogContentText>
          </DialogContent>
        </form>
        {deleteVisible && calendar?.value ? (
          <Prompt
            heading="Delete Calendar"
            text={`Are you sure you want to delete the calendar: ${calendar?.label}.`}
            visible={deleteVisible}
            okText="Delete"
            okColor="error"
            handleOk={() => {
              handleConfirmDelete();
            }}
            handleClose={() => setDeleteVisible(false)}
          />
        ) : null}
      </Dialog>
    );
  }
);

export default CreateCalendarModal;
